import { Capacitor } from '@capacitor/core';
import { useCallback, useMemo, useState } from 'react';

import { CountryListItem, DateTimePicker, TextLink } from '@/components';
import { ButtonSmall } from '@/components/Button';
import { Autocomplete, Checkbox } from '@/components/Inputs';
import { LoaderSpinner } from '@/components/LoaderSpinner/LoaderSpinner';
import SelectionChip from '@/components/SelectionChip/SelectionChip';
import { useCountrySearch } from '@/hooks/useCountrySearch';
import { type GeolocationIssue, openLocationSettings } from '@/hooks/useGeolocation';
import { Address, type PlaceDetails } from '@/pages/ServiceProviders/ServiceProviderForms';
import type { MappedPersonForSelection } from '@/store/persons/person.dto';

import s from './MainFields.module.scss';
import { UserPickerModal } from './UserPickerModal';

interface MainFieldsProps {
  location: string;
  country: string;
  date: string;
  sternTo: boolean;
  alongside: boolean;
  anchored: boolean;
  watchkeeperIds: string[];
  allUsers: MappedPersonForSelection[];
  geoPlace?: PlaceDetails | null;
  /** Device location is unavailable for a reason the user can fix; shown under an empty Location. */
  geoIssue?: GeolocationIssue;
  /** Device location is still being looked up; shown under an empty Location. */
  geoLocating?: boolean;
  disabled?: boolean;
  onChange: (field: 'location' | 'country' | 'date', value: string) => void;
  onMooringChange: (field: 'sternTo' | 'alongside' | 'anchored', value: boolean) => void;
  onAddWatchkeeper: (userId: string) => void;
  onRemoveWatchkeeper: (userId: string) => void;
}

export function MainFields({
  location,
  country,
  date,
  sternTo,
  alongside,
  anchored,
  watchkeeperIds,
  allUsers,
  geoPlace,
  geoIssue,
  geoLocating,
  disabled,
  onChange,
  onMooringChange,
  onAddWatchkeeper,
  onRemoveWatchkeeper,
}: MainFieldsProps) {
  const [place, setPlace] = useState<PlaceDetails | null | undefined>(undefined);
  const geoMatchesLocation = !!geoPlace && geoPlace.formattedAddress === location;
  const effectivePlace = location
    ? place !== undefined
      ? place
      : geoMatchesLocation
        ? geoPlace
        : null
    : null;
  const [userPickerOpen, setUserPickerOpen] = useState(false);
  const [countrySearchQuery, setCountrySearchQuery] = useState('');

  const handleLocationChange = useCallback(
    (place: PlaceDetails | null) => {
      setPlace(place);
      onChange('location', place?.formattedAddress ?? '');
      if (place) {
        const code = place.addressComponents?.find((c) => c.types?.includes('country'))?.shortText;
        if (code) onChange('country', code.toUpperCase());
      }
    },
    [onChange],
  );

  const { countries, filteredCountries } = useCountrySearch(countrySearchQuery);

  const userById = useMemo(() => new Map(allUsers.map((u) => [u.id, u])), [allUsers]);
  const watchkeepers = watchkeeperIds
    .map((id) => userById.get(id))
    .filter(Boolean) as MappedPersonForSelection[];
  const disabledIds = new Set(watchkeeperIds);

  return (
    <div className={s.root}>
      <div className={s.fieldsGrid}>
        <div className={s.fieldCell}>
          <label className={s.stackLabel}>Location</label>
          <Address
            key={date}
            place={effectivePlace}
            onPlaceChange={handleLocationChange}
            initialInput={location}
            label=""
            disabled={disabled}
          />
          {geoLocating && !location && !disabled && (
            <div className={`${s.geoHint} ${s.locating}`}>
              <LoaderSpinner size={16} fullHeight={false} label="" />
              Locating…
            </div>
          )}
          {geoIssue && !location && !disabled && (
            <p className={s.geoHint}>
              {geoIssue === 'off' ? 'Location services are off.' : 'Location access is off.'}{' '}
              {Capacitor.isNativePlatform() && (
                <TextLink onClick={() => openLocationSettings(geoIssue)}>Open Settings</TextLink>
              )}
            </p>
          )}
        </div>
        <div className={s.fieldCell}>
          <label className={s.stackLabel}>Country</label>
          <Autocomplete<string>
            label=""
            placeholder="France"
            disabled={disabled}
            value={countrySearchQuery}
            onChange={setCountrySearchQuery}
            selectedOptionLabel={(() => {
              const c = countries.find((c) => c.code === country);
              return c ? `${c.code} - ${c.name}` : country || null;
            })()}
            onItemSelect={(code) => {
              onChange('country', code ?? '');
              setCountrySearchQuery('');
            }}
            options={filteredCountries.map((c) => ({
              label: <CountryListItem code={c.code} name={c.name} />,
              value: c.code,
            }))}
          />
        </div>
        <div className={s.fieldCell}>
          <label className={s.stackLabel}>Date</label>
          <DateTimePicker
            placeholder="Jul 18, 2026"
            value={date}
            onChange={(value) => onChange('date', value)}
            maxDate={new Date()}
            showTimeInput={false}
          />
        </div>
      </div>

      <fieldset
        disabled={disabled}
        style={{ border: 0, padding: 0, margin: 0, minInlineSize: 'auto' }}
      >
        <section className={s.section}>
          <div className={s.mooringGrid}>
            <div className={s.mooringCell}>
              <Checkbox
                id="mooring-stern-to"
                label="Stern To"
                checked={sternTo}
                disabled={disabled}
                onChange={(event) => onMooringChange('sternTo', event.target.checked)}
              />
            </div>
            <div className={s.mooringCell}>
              <Checkbox
                id="mooring-alongside"
                label="Alongside"
                checked={alongside}
                disabled={disabled}
                onChange={(event) => onMooringChange('alongside', event.target.checked)}
              />
            </div>
            <div className={s.mooringCell}>
              <Checkbox
                id="mooring-anchored"
                label="Anchored"
                checked={anchored}
                disabled={disabled}
                onChange={(event) => onMooringChange('anchored', event.target.checked)}
              />
            </div>
          </div>
        </section>

        <div className={s.watchkeeperRow}>
          <p className={s.watchkeeperLabel}>Watchkeepers:</p>
          <div className={s.chips}>
            {watchkeepers.map((user) => (
              <SelectionChip
                key={user.id}
                label={`${user.firstName} ${user.lastName}`}
                variant="primary"
                size="sm"
                onRemove={() => onRemoveWatchkeeper(user.id)}
              />
            ))}
          </div>
          <ButtonSmall type="button" onClick={() => setUserPickerOpen(true)}>
            +
          </ButtonSmall>

          <UserPickerModal
            open={userPickerOpen}
            users={allUsers}
            disabledIds={disabledIds}
            onSelect={(user) => {
              onAddWatchkeeper(user.userId);
              setUserPickerOpen(false);
            }}
            onClose={() => setUserPickerOpen(false)}
          />
        </div>
      </fieldset>
    </div>
  );
}
